import React, { useState, useRef } from 'react';
import { Camera, Edit2, Save, X, DollarSign, User, Mail, Phone, MapPin } from 'lucide-react';
import { updateRepSalary, uploadRepPhoto, updateRepStatus } from '../services/api';

export default function RepProfileTab({ rep, onUpdate }) {
  const [editingSalary, setEditingSalary] = useState(false);
  const [salaryInput, setSalaryInput] = useState((rep.salary_agreed != null && !Number.isNaN(Number(rep.salary_agreed))) ? Number(rep.salary_agreed).toString() : '');
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const handleSaveSalary = async () => {
    const parsed = parseFloat(salaryInput);
    if (Number.isNaN(parsed) || parsed < 0) {
      alert('Invalid salary amount');
      return;
    }
    setSaving(true);
    try {
      const result = await updateRepSalary(rep.id, parsed);
      if (result.success) {
        onUpdate({ ...rep, salary_agreed: parsed });
        setEditingSalary(false);
      } else {
        alert(result.error || 'Failed to update salary');
      }
    } catch (err) {
      // Salary update error handled silently
      alert('Failed to update salary');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    const newStatus = rep.status === 'active' ? 'disabled' : 'active';
    setSaving(true);
    try {
      const result = await updateRepStatus(rep.id, newStatus);
      if (result.success) {
        onUpdate({ ...rep, status: newStatus });
      } else {
        alert(result.error || 'Failed to update status');
      }
    } catch (err) {
      // Status update error handled silently
      alert('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      alert('Only JPEG and PNG images are allowed');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result.split(',')[1]; // strip data:...;base64,
      try {
        const result = await uploadRepPhoto(rep.id, base64, file.type);
        if (result.success) {
          onUpdate({ ...rep, photo_url: result.data.rep.photo_url });
        } else {
          alert(result.error || 'Failed to upload photo');
        }
      } catch (err) {
        // Photo upload error handled silently
        alert('Failed to upload photo');
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Profile Header */}
      <div className="flex items-start gap-6">
        {/* Photo */}
        <div className="relative group">
          {rep.photo_url ? (
            <img src={rep.photo_url} alt={rep.name} className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg" />
          ) : (
            <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-2xl font-bold shadow-lg">
              {rep.name?.charAt(0) || '?'}
            </div>
          )}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="absolute bottom-0 right-0 bg-primary-900 text-white p-1.5 rounded-full shadow hover:bg-primary-800 transition"
            title="Upload photo"
          >
            <Camera size={16} />
          </button>
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            className="hidden"
            ref={fileInputRef}
            onChange={handlePhotoUpload}
          />
        </div>

        {/* Info */}
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{rep.name}</h2>
          <div className="space-y-1 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Mail size={14} className="text-gray-400" />
              {rep.email}
            </div>
            {rep.phone && (
              <div className="flex items-center gap-2">
                <Phone size={14} className="text-gray-400" />
                {rep.phone}
              </div>
            )}
            {rep.town && (
              <div className="flex items-center gap-2">
                <MapPin size={14} className="text-gray-400" />
                {rep.town}
              </div>
            )}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${
              rep.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {rep.status === 'active' ? 'Active' : 'Disabled'}
            </span>
            <button
              onClick={handleToggleStatus}
              disabled={saving}
              className={`text-xs font-medium px-2 py-1 rounded-full transition ${
                rep.status === 'active'
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              } disabled:opacity-50`}
            >
              {rep.status === 'active' ? 'Disable' : 'Enable'}
            </button>
            <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-100 text-gray-700">
              Role: {rep.role || 'rep'}
            </span>
          </div>
        </div>
      </div>

      {/* Salary */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <DollarSign size={18} className="text-primary-900" />
            <span className="font-semibold text-gray-900">Agreed Salary</span>
          </div>
          {!editingSalary && (
            <button
              onClick={() => setEditingSalary(true)}
              className="text-primary-900 hover:text-primary-700 transition"
              title="Edit salary"
            >
              <Edit2 size={16} />
            </button>
          )}
        </div>
        {editingSalary ? (
          <div className="flex items-center gap-2">
            <input
              type="number"
              step="0.01"
              min="0"
              value={salaryInput}
              onChange={(e) => setSalaryInput(e.target.value)}
              className="border rounded px-2 py-1 w-32 text-sm"
              placeholder="0.00"
            />
            <button
              onClick={handleSaveSalary}
              disabled={saving}
              className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition disabled:opacity-50"
            >
              {saving ? <Save size={14} className="animate-pulse" /> : <Save size={14} />}
            </button>
            <button
              onClick={() => {
                setEditingSalary(false);
                setSalaryInput((rep.salary_agreed != null && !Number.isNaN(Number(rep.salary_agreed))) ? Number(rep.salary_agreed).toString() : '');
              }}
              className="text-gray-500 hover:text-gray-700 transition"
              title="Cancel"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <p className="text-gray-700">
            {rep.salary_agreed != null && !Number.isNaN(Number(rep.salary_agreed)) ? `₹${Number(rep.salary_agreed).toFixed(2)}` : 'Not set'}
          </p>
        )}
      </div>
    </div>
  );
}
